import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Typography, IconButton, useTheme, TextField, Button, CardMedia } from '@mui/material';

import CanvasApi from '../../../services/CanvasApi';
import CloseIcon from '../../../landingPage/assets/icons/CloseIcon';
import { grey } from '@mui/material/colors';
import { useDropzone } from 'react-dropzone';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GenerateImageResponse } from '../../../types/CanvaGenerateInterface';


export const MODAL_NAME = 'RemoveBackgroundModal';
export const BACKGROUND_REFERENCE_IMAGE = 'BackgroundReferenceImage';
export const BACKGROUND_PROMPT = 'BackgroundPrompt';

interface RemoveBackgroundModalProps {
  canvasApi: CanvasApi;
}

const RemoveBackgroundModal: React.FC<RemoveBackgroundModalProps> = ({ canvasApi }) => {
  const theme = useTheme();
  const [displayModal, setDisplayModal] = useState(false);
  const [backgroundPrompt, setBackgroundPrompt] = useState<string>();
  const [backgroundReferenceImage, setBackgroundReferenceImage] = useState<File>();

  canvasApi.registerModalControl(MODAL_NAME, displayModal, setDisplayModal);
  canvasApi.registerComponentState(BACKGROUND_REFERENCE_IMAGE, backgroundReferenceImage, setBackgroundReferenceImage);
  canvasApi.registerComponentState(BACKGROUND_PROMPT, backgroundPrompt, setBackgroundPrompt);
  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    canvasApi.hideModal(MODAL_NAME);
  };

  const replaceUsingPrompt = async () => {
    canvasApi.executeImageGeneration(async requestData => ({
      ...requestData,
      prompt: backgroundPrompt,
    }), async (client, request) => {
      const { data }: { data: GenerateImageResponse } = await client.replaceSubjectBackground(request);
      return data;
    }).then(() => {
      setBackgroundPrompt(undefined);
      setBackgroundReferenceImage(undefined);
    });
    canvasApi.hideAllModals();
  };

  const replaceUsingImage = () => {
    canvasApi.executeImageGeneration(async request => ({
      ...request,
      reference_image: backgroundReferenceImage ? await toBase64(backgroundReferenceImage) : undefined,
    }), async (client, request) => {
      const { data }: { data: GenerateImageResponse } = await client.replaceSubjectBackground(request);
      return data;
    }).then(() => {
      setBackgroundPrompt(undefined);
      setBackgroundReferenceImage(undefined);
    });
    canvasApi.hideAllModals();
  };

  const handlers = {
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      setBackgroundReferenceImage(file);
    },
    onRemoveBackgroundReferenceImage: () => {
      setBackgroundReferenceImage(undefined);
    }
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handlers.onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png']
    },
    maxFiles: 1
  });

  return (
    <Dialog open={displayModal}
      fullWidth={true}
      maxWidth="xl"
      onClose={(event, reason) => handleClose(event, reason)}>
      <DialogTitle>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Typography sx={{
            fontSize: '2rem',
            fontWeight: 'bold',
          }}>Replace Background</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <IconButton
          aria-label="close"
          onClick={() => canvasApi.hideModal(MODAL_NAME)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{
          height: '50vh',
          display: 'flex',
          flexDirection: 'column',
        }}>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',

          }}>
            <Typography >Replace the background of the canvas image using one of the following methods:</Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              width: '50%',
              height: '100%',
              p: '1em'
            }}>
              <Box sx={{
                order: '0',
                mb: '1em',
              }}>
                <Typography sx={{
                  borderBottom: `1px solid ${grey[800]}`,
                  fontWeight: 'bold',
                }}>With a description:</Typography>
              </Box>
              <Box sx={{
                order: '1',
                flex: '1 1 auto',
              }}>
                <TextField
                  id='replace-background-prompt'
                  variant="outlined"
                  fullWidth
                  placeholder="type a prompt..."
                  value={backgroundPrompt || ''}
                  onChange={e => setBackgroundPrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      replaceUsingPrompt();
                    }
                  }}
                  multiline
                  maxRows={6}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      fontWeight: theme.typography.fontWeightRegular,
                      padding: theme.spacing(0, 3),
                      '&::placeholder': {
                        fontWeight: theme.typography.fontWeightLight
                      },
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.default} inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.default
                      }
                    },
                    '& .MuiOutlinedInput-root': {
                      background: 'black',
                      padding: 1,
                      borderRadius: 7,
                      '& fieldset': {
                        border: `1px solid ${grey[800]}`
                      },
                      '&:hover fieldset': {
                        border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}`
                      },
                      '&.Mui-focused fieldset': {
                        border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}`
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{
                order: '99',
                alignSelf: 'flex-end',
              }}>
                <Button
                  disableElevation
                  size='large'
                  variant="contained"
                  color="secondary"
                  onClick={replaceUsingPrompt}
                  sx={{
                    width: 160,
                    px: 4,
                    textTransform: 'none',
                    borderRadius: 50,
                  }}
                >
                  <Typography fontWeight={'medium'}>Replace Background</Typography>
                </Button>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              width: '50%',
              height: '100%',
              borderLeft: `1px solid ${grey[800]}`,
              p: '1em'
            }}>
              <Box sx={{
                order: '0',
                mb: '1em',
                borderBottom: `1px solid ${grey[800]}`,

              }}>

                <Typography >With a Reference Image:</Typography>
              </Box>
              <Box sx={{
                order: '1',
                flex: '1 1 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: '1em'
              }}>

                {backgroundReferenceImage && <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  maxHeight: '100%',
                  maxWidth: '100%',
                }}>

                  <CardMedia
                    component="img"
                    src={URL.createObjectURL(backgroundReferenceImage)}
                    sx={{
                      order: '0',
                      height: '20em',
                      width: '20em',
                      objectFit: 'cover'
                    }}
                  >
                  </CardMedia>
                  <IconButton
                    onClick={() => handlers.onRemoveBackgroundReferenceImage()}
                    sx={{
                      order: '99',
                      color: 'white',
                      position: 'relative',
                      top: 0,
                      right: 0,
                      zIndex: 2
                    }}
                    size="small"
                    className="delete-icon"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                }

                {!backgroundReferenceImage && <Box
                  {...getRootProps()}
                  sx={{
                    height: '20em',
                    width: '20em',

                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px dashed ${theme.palette.divider}`,
                    bgcolor: theme.custom.card.background.secondary,
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative'
                  }}     >
                  <input {...getInputProps()} />
                  <Typography color={theme => theme.palette.text.primary} sx={{ p: 0.5 }}>
                    Drop Background Reference Image Here
                  </Typography>
                </Box>}

              </Box>
              <Box
                sx={{
                  order: '99',
                  alignSelf: 'flex-end',
                }}>
                <Button
                  disableElevation
                  size='large'
                  variant="contained"
                  color="secondary"
                  onClick={replaceUsingImage}
                  sx={{
                    width: 160,
                    px: 4,
                    textTransform: 'none',
                    borderRadius: 50
                  }}
                >
                  <Typography fontWeight={'medium'}>Replace Using Image</Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog >
  );
};

export default RemoveBackgroundModal;